import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { 
  ChevronRight, 
  ChevronLeft, 
  CheckCircle, 
  Camera,
  Thermometer,
  Music,
  Smartphone,
  MessageSquare,
  DollarSign,
  Users,
  Clock,
  Store
} from "lucide-react";

// Quiz questions with multi-select support
const QUIZ_QUESTIONS = [
  { 
    id: "pos", 
    question: "What POS system do you use?", 
    multiSelect: false,
    icon: Store,
    options: [
      "Toast", 
      "Square", 
      "Clover / Lightspeed", 
      "Aloha / Micros / NCR", 
      "Other / None"
    ] 
  },
  { 
    id: "locations", 
    question: "How many locations?", 
    multiSelect: false,
    icon: Store,
    options: [
      "1 location", 
      "2-3 locations", 
      "4-10 locations", 
      "10+ locations"
    ] 
  },
  { 
    id: "covers", 
    question: "Average daily covers?", 
    multiSelect: false,
    icon: Users,
    options: [
      "Under 50", 
      "50-100", 
      "100-200", 
      "200+"
    ] 
  },
  { 
    id: "pain_points", 
    question: "Biggest pain points? (select all that apply)", 
    multiSelect: true,
    icon: Clock,
    options: [
      "Missed phone calls / reservations",
      "Staff turnover & training",
      "Food waste & inventory",
      "Scheduling & labor costs",
      "Kitchen-floor communication",
      "Quality consistency",
      "Customer complaints"
    ] 
  },
  { 
    id: "cameras", 
    question: "What camera coverage do you have?", 
    multiSelect: true,
    icon: Camera,
    options: [
      "Kitchen / line cameras",
      "Dining room cameras",
      "Prep area cameras",
      "Walk-in / storage cameras",
      "Bar area cameras",
      "No cameras currently",
      "Planning to add cameras"
    ] 
  },
  { 
    id: "sensors", 
    question: "Any temperature or IoT sensors?", 
    multiSelect: true,
    icon: Thermometer,
    options: [
      "Walk-in cooler sensors",
      "Freezer sensors",
      "HVAC / thermostat sensors",
      "No sensors currently",
      "Interested in adding"
    ] 
  },
  { 
    id: "systems", 
    question: "Other systems you use? (select all)", 
    multiSelect: true,
    icon: Music,
    options: [
      "Background music (Sonos/Spotify)",
      "Smart lighting controls",
      "Smart HVAC / thermostat",
      "Reservation system (OpenTable/Resy)",
      "Inventory management software",
      "Scheduling software",
      "None of these"
    ] 
  },
  { 
    id: "devices", 
    question: "What devices does your team use?", 
    multiSelect: true,
    icon: Smartphone,
    options: [
      "iPhones",
      "Android phones",
      "Apple Watches",
      "Smart glasses",
      "Earpieces / headsets",
      "KDS screens",
      "Tablets",
      "Basic phones only"
    ] 
  },
  { 
    id: "communication", 
    question: "How does your team communicate now?", 
    multiSelect: false,
    hasOtherInput: true,  // Enable text input for "Other"
    icon: MessageSquare,
    options: [
      "Group texts",
      "WhatsApp",
      "Walkie-talkies",
      "We just yell",
      "Other"
    ] 
  },
  { 
    id: "budget", 
    question: "Monthly tech budget range?", 
    multiSelect: false,
    icon: DollarSign,
    options: [
      "Under $200/month",
      "$200-500/month",
      "$500-1000/month",
      "Over $1000/month",
      "Not sure yet"
    ] 
  },
];

export const InChatQuiz = ({ onComplete, onCancel }) => {
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [otherInputs, setOtherInputs] = useState({});  // For "Other" text inputs
  const [email, setEmail] = useState("");
  const [restaurantName, setRestaurantName] = useState("");
  const [showEmailStep, setShowEmailStep] = useState(false);

  const q = QUIZ_QUESTIONS[currentQ];
  const progress = ((currentQ + 1) / QUIZ_QUESTIONS.length) * 100;
  const currentAnswer = answers[q.id];

  const handleSingleSelect = (value) => {
    setAnswers(prev => ({ ...prev, [q.id]: value }));
  };

  const handleMultiSelect = (option) => {
    const current = answers[q.id] || [];
    const isSelected = current.includes(option);
    
    // Handle "None" type options - clear others when selected
    const isNoneOption = option.toLowerCase().includes("none") || option.toLowerCase().includes("no ");
    const hasNoneOption = current.some(o => o.toLowerCase().includes("none") || o.toLowerCase().includes("no "));
    
    if (isNoneOption) {
      // Selecting "none" clears all other selections
      setAnswers(prev => ({ ...prev, [q.id]: isSelected ? [] : [option] }));
    } else if (hasNoneOption) {
      // Selecting something else when "none" is selected - replace with new selection
      setAnswers(prev => ({ ...prev, [q.id]: [option] }));
    } else {
      // Normal toggle
      setAnswers(prev => ({
        ...prev,
        [q.id]: isSelected 
          ? current.filter(o => o !== option)
          : [...current, option]
      }));
    }
  };

  const canProceed = () => {
    if (q.multiSelect) {
      return (answers[q.id] || []).length > 0;
    }
    return !!answers[q.id];
  };

  const handleNext = () => {
    if (currentQ < QUIZ_QUESTIONS.length - 1) {
      setCurrentQ(p => p + 1);
    } else {
      setShowEmailStep(true);
    }
  };

  const handlePrev = () => {
    if (showEmailStep) {
      setShowEmailStep(false);
    } else if (currentQ > 0) {
      setCurrentQ(p => p - 1);
    }
  };

  const handleSubmit = () => {
    // Format multi-select answers as arrays, single as strings
    const formattedAnswers = {};
    QUIZ_QUESTIONS.forEach(question => {
      const answer = answers[question.id];
      formattedAnswers[question.id] = question.multiSelect 
        ? (answer || []) 
        : (answer || "");
    });

    onComplete({
      answers: formattedAnswers,
      email: email || undefined,
      restaurant_name: restaurantName || undefined,
      // Flatten for easy access
      pos_system: formattedAnswers.pos,
      locations: formattedAnswers.locations,
      covers: formattedAnswers.covers,
      pain_points: formattedAnswers.pain_points,
      cameras: formattedAnswers.cameras,
      sensors: formattedAnswers.sensors,
      systems: formattedAnswers.systems,
      devices: formattedAnswers.devices,
      communication: formattedAnswers.communication,
      budget: formattedAnswers.budget,
    });
  };

  // Email collection step
  if (showEmailStep) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#0A1244] rounded-xl border border-gold/30 p-4 my-3"
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center">
            <CheckCircle className="w-3 h-3 text-gold" />
          </div>
          <span className="text-xs text-gold font-medium">Almost done!</span>
        </div>
        
        <p className="text-slate-400 text-xs mb-4">
          Where should I send your personalized savings report?
        </p>
        
        <div className="space-y-3 mb-4">
          <Input 
            value={restaurantName} 
            onChange={e => setRestaurantName(e.target.value)} 
            placeholder="Restaurant name"
            className="bg-indigo-700/30 border-gold/20 text-white text-sm placeholder:text-slate-500 focus:border-gold h-9"
          />
          <Input 
            type="email" 
            value={email} 
            onChange={e => setEmail(e.target.value)} 
            placeholder="your@email.com"
            className="bg-indigo-700/30 border-gold/20 text-white text-sm placeholder:text-slate-500 focus:border-gold h-9"
          />
        </div>
        
        <div className="flex justify-between gap-2">
          <Button 
            onClick={handlePrev} 
            variant="ghost" 
            size="sm"
            className="text-slate-400 hover:text-white hover:bg-indigo-700/30"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button 
            onClick={handleSubmit}
            size="sm"
            className="bg-gold hover:bg-gold/90 text-indigo-900 font-medium"
          >
            Get My Report <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </motion.div>
    );
  }

  const Icon = q.icon;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#0A1244] rounded-xl border border-gold/30 p-4 my-3"
      data-testid="in-chat-quiz"
    >
      {/* Progress */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] text-slate-500">
          Question {currentQ + 1} of {QUIZ_QUESTIONS.length}
        </span>
        <div className="flex-1 mx-3 h-1 bg-indigo-700/50 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-gold"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        <span className="text-[10px] text-gold">{Math.round(progress)}%</span>
      </div>

      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentQ}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-gold/10 flex items-center justify-center">
              <Icon className="w-4 h-4 text-gold" />
            </div>
            <h4 className="text-white text-sm font-medium">{q.question}</h4>
          </div>

          {/* Options */}
          <div className="space-y-2 mb-4">
            {q.multiSelect ? (
              // Multi-select with checkboxes
              q.options.map((opt, idx) => {
                const isSelected = (currentAnswer || []).includes(opt);
                return (
                  <label
                    key={idx}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all text-sm ${
                      isSelected 
                        ? "bg-gold/10 border-gold/40 text-white" 
                        : "bg-indigo-700/20 border-indigo-500/30 text-slate-300 hover:border-slate-500"
                    }`}
                    onClick={() => handleMultiSelect(opt)}
                  >
                    <Checkbox 
                      checked={isSelected}
                      className="border-gold/50 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                    />
                    <span className="text-xs">{opt}</span>
                  </label>
                );
              })
            ) : (
              // Single select with radio
              <>
                <RadioGroup 
                  value={currentAnswer?.startsWith("Other:") ? "Other" : (currentAnswer || "")} 
                  onValueChange={(val) => {
                    if (val === "Other") {
                      handleSingleSelect("Other:");
                    } else {
                      handleSingleSelect(val);
                      setOtherInputs(prev => ({ ...prev, [q.id]: "" }));
                    }
                  }}
                  className="space-y-2"
                >
                  {q.options.map((opt, idx) => (
                    <Label
                      key={idx}
                      htmlFor={`q${q.id}-o${idx}`}
                      className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                        (opt === "Other" ? currentAnswer?.startsWith("Other:") : currentAnswer === opt)
                          ? "bg-gold/10 border-gold/40 text-white" 
                          : "bg-indigo-700/20 border-indigo-500/30 text-slate-300 hover:border-slate-500"
                      }`}
                    >
                      <RadioGroupItem 
                        value={opt} 
                        id={`q${q.id}-o${idx}`}
                        className="border-gold/50 text-gold"
                      />
                      <span className="text-xs">{opt}</span>
                    </Label>
                  ))}
                </RadioGroup>
                
                {/* Other text input */}
                {q.hasOtherInput && currentAnswer?.startsWith("Other:") && (
                  <Input
                    value={otherInputs[q.id] || ""}
                    onChange={(e) => {
                      setOtherInputs(prev => ({ ...prev, [q.id]: e.target.value }));
                      handleSingleSelect(`Other: ${e.target.value}`);
                    }}
                    placeholder="Please specify..."
                    className="mt-2 bg-indigo-700/30 border-gold/20 text-white text-sm placeholder:text-slate-500 focus:border-gold h-9"
                    autoFocus
                  />
                )}
              </>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex justify-between gap-2">
        <Button 
          onClick={currentQ === 0 ? onCancel : handlePrev} 
          variant="ghost" 
          size="sm"
          className="text-slate-400 hover:text-white hover:bg-indigo-700/30"
          data-testid={currentQ === 0 ? "quiz-cancel-btn" : "quiz-back-btn"}
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> 
          {currentQ === 0 ? "Cancel" : "Back"}
        </Button>
        <Button 
          onClick={handleNext}
          disabled={!canProceed()}
          size="sm"
          className="bg-gold hover:bg-gold/90 text-indigo-900 font-medium disabled:opacity-40"
          data-testid="quiz-next-btn"
        >
          {currentQ === QUIZ_QUESTIONS.length - 1 ? "Finish" : "Next"} 
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </motion.div>
  );
};

export default InChatQuiz;
